import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { api } from '../../../api/client';
import { ICM_LIST_TAKE } from '../icmPaging';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { ExportMenu } from '../../../components/AppToolbar/ExportMenu';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { fmtLocalDate, fmtLocalShort } from '../../../utils/datetime';
import { downloadBlob } from '../../../utils/exportData';
import { DateFilterSelect, monthsOf, DEFAULT_DATE_FILTER, type DateFilterId } from './IcmDateFilter';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './vendorCoa.css';

/** One row of the "Item Partial Consumption" grid inside the LT popup --
 *  a dbo.ItemDepletion row, as AI_ICM_Consumption_Get_SP's second result set
 *  returns it. */
interface DepletionRow {
  oid: string;
  date: string | null;
  stockAmount: number | null;
  amountUnitsName: string | null;
  amountTaken: number;
  amountRemain: number;
  consumedLt: string | null;
  takenByName: string | null;
  consumedByName: string | null;
  comment: string | null;
  lastUpdatedByName: string | null;
  lastUpdatedDate: string | null;
}

/** The lot behind one LT# row, as the popup shows it (read-only). */
interface LtDetail {
  lt: string | null;
  itemName: string | null;
  itemSpecification: string | null;
  size: string | null;
  catalog: string | null;
  category: string | null;
  /** "Stock Amount" = dbo.Items.Amount, a STRING (legacy binds Item.Amount). */
  itemAmount: string | null;
  isDeplete: boolean;
  vendorLt: string | null;
  expiryDate: string | null;
  vendorName: string | null;
  storageName: string | null;
  /* The ids behind the Vendor / Storage links. */
  vendorId: string | null;
  storageId: string | null;
}

/** dbo.Vendors as Vendors_DetailView lays it out (two columns). */
interface VendorRef {
  vendorcode: string | null; vendorName: string | null; account: string | null;
  address1: string | null; address2: string | null; city: string | null;
  state: string | null; stateCbName: string | null; zipCode: string | null;
  country: string | null; phone: string | null; fax: string | null;
  contact: string | null; email: string | null; qualification: string | null;
  certificate: string | null; certExpDate: string | null; approvedDate: string | null;
  approvedByName: string | null; retiredDate: string | null; retiredByName: string | null;
  website: string | null; comment: string | null;
}

/** dbo.ICMStorage as ICMStorage_DetailView lays it out. */
interface StorageRef {
  storageName: string | null; location: string | null;
  active: boolean | null; comment: string | null;
}

/** One certificate PDF attached to a record (the Upload tab). */
interface VendorCoaAttachment {
  oid: string;
  fileDataOid: string | null;
  fileName: string | null;
  sizeBytes: number;
}

interface VendorCoaRow {
  oid: string;
  vendorLt: string | null;
  vendorName: string | null;
  itemName: string | null;
  catelog: string | null;
  requestorName: string | null;
  loadedDate: string | null;
  rqid: string | null;
  /* The live GoLive grid: Department, PO# and RC# alongside the rest. */
  department: string | null;
  poidText: string | null;
  receiveId: string | null;
  /* Not columns -- the Edit dialog uses them (file name to match on, the
     attached PDF, how many there are). */
  destination: string | null;
  fileDataOid: string | null;
  attachmentCount: number;
}

interface VendorCoaDetail {
  oid: string;
  /* The Get endpoint has always returned these; only this interface lacked them,
     so the detail's Loaded By / Loaded Date read blank. */
  loadedByName: string | null;
  loadedDate: string | null;
  vendorLt: string | null;
  vendorId: string | null;
  vendorName: string | null;
  itemId: string | null;
  itemName: string | null;
  catelog: string | null;
  requestorId: string | null;
  requestorName: string | null;
  department: string | null;
  rqid: string | null;
  poId: string | null;
  receiveId: string | null;
  comments: string | null;
}

interface IdNameOption { id: string; name: string; }

interface CoaForm {
  vendorLt: string;
  vendorId: string;
  itemId: string;
  catelog: string;
  requestorId: string;
  department: string;
  rqid: string;
  poId: string;
  receiveId: string;
  comments: string;
}

const EMPTY_FORM: CoaForm = {
  vendorLt: '', vendorId: '', itemId: '', catelog: '',
  requestorId: '', department: '', rqid: '', poId: '',
  receiveId: '', comments: '',
};

export default function VendorCoaListPage() {
  const notify = useNotify();
  const saveFlow = useSaveFlow();

  const [rows, setRows] = useState<VendorCoaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Dialog state
  const [dialog, setDialog] = useState<{ mode: 'new' | 'edit'; id?: string } | null>(null);
  const [form, setForm] = useState<CoaForm>(EMPTY_FORM);

  /* Date Filter + History -- legacy VRCDateFilter_Execute and ShowViewModeVRC,
     both of which window LoadedDate; History is the filter's ALL branch,
     "[LoadedDate] IS Not NULL AND [LoadedBy] IS Not NULL", i.e. only
     certificates that have actually been uploaded. */
  /* The MAIN list carries no Date Filter -- GoLive's VendorReagentCertificate
     ListView has none -- but its HISTORY view (Vendors COA History) does, and
     opens on 3M. So the control follows the mode rather than sitting on the bar
     permanently (owner request 2026-09-29, with the GoLive History screenshot):
     All Records = no filter, History = the picker, defaulting to 3M. */
  const [dateFilter, setDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);
  const [history, setHistory] = useState(false);

  /** Enter/leave the History view. Entering adopts GoLive's 3M default;
   *  leaving drops back to the unfiltered list. */
  function setHistoryMode(on: boolean) {
    setHistory(on);
    setDateFilter(on ? '3M' : 'ALL');
    setSelected(new Set());
  }
  const [importing, setImporting] = useState(false);
  /** Detail view (legacy VendorReagentCertificate_DetailView). Standing rule 12:
   *  Edit REPLACES the list with a full-page detail, never a popup. */
  const [detailTab, setDetailTab] = useState<'upload' | 'lt'>('upload');
  const [attachments, setAttachments] = useState<VendorCoaAttachment[]>([]);
  const [lts, setLts] = useState<{ oid: string; lt: string | null }[]>([]);
  /* The LT tab's row popup (legacy "Fractional Consumption"). */
  const [ltPopupOpen, setLtPopupOpen] = useState(false);
  const [ltDetail, setLtDetail] = useState<LtDetail | null>(null);
  const [ltDepletions, setLtDepletions] = useState<DepletionRow[]>([]);
  const [ltPopupTab, setLtPopupTab] = useState<'consumption' | 'uploads'>('consumption');
  /* Vendor Lot is the ONE field this popup can change (legacy marks every
     other AllowEdit="False" and leaves VendorLT editable), so it is held
     separately from the read-only detail and committed by OK. */
  const [ltVendorLt, setLtVendorLt] = useState('');
  const [ltSaving, setLtSaving] = useState(false);
  const [ltOid, setLtOid] = useState<string | null>(null);
  /* The reference popups the LT popup's Vendor / Storage links open. Legacy
     draws a read-only lookup editor as a HYPERLINK into that record's own
     detail view, so both are a further modal, stacked over this one. */
  const [refPopup, setRefPopup] = useState<'vendor' | 'storage' | null>(null);
  const [vendorRef, setVendorRef] = useState<VendorRef | null>(null);
  const [storageRef, setStorageRef] = useState<StorageRef | null>(null);
  const { confirm: confirmDelete } = useConfirm();
  // MAC rights (hide, never grey): Edit / Save / Import / attach / the Vendor Lot edit = Write, attachment Delete = Delete.
  const perm = useNavActions('icm-vendor-coa');
  /** Upload-tab selection -- Delete and Download act on the ticked row. */
  const [uploadSel, setUploadSel] = useState<Set<string | number>>(new Set());
  const selectedUpload = uploadSel.size === 1 ? String(Array.from(uploadSel)[0]) : null;
  /** The one ticked attachment ROW (the toolbar Download acts on a single
   *  file; Delete acts on the whole ticked set — see removeAttachments). */
  const selectedUploadRow = selectedUpload
    ? attachments.find(a => a.oid === selectedUpload) ?? null
    : null;
  /* ── The LT popup's two grids ───────────────────────────────────────────
     "Item Partial Consumption" carries legacy's full column list in its order;
     every one is a stored dbo.ItemDepletion column (the SP was extended to
     return the ones the editing screen recomputes locally). "Consumed" is the
     row's ConsumedLT, and TakenBy is a DIFFERENT person from ConsumedBy --
     legacy shows both, so both are here. */
  const depPopupCols: AppColumn<DepletionRow>[] = [
    { field: 'date',            header: 'Date',        width: 130,
      text: r => fmtLocalDate(r.date) ?? '', render: r => fmtLocalDate(r.date) ?? '' },
    { field: 'stockAmount',     header: 'StockAmount', width: 110, type: 'number',
      text: r => (r.stockAmount == null ? '' : String(r.stockAmount)), value: r => r.stockAmount ?? 0 },
    { field: 'amountUnitsName', header: 'AmountUnits', width: 110,
      text: r => r.amountUnitsName ?? '', render: r => r.amountUnitsName ?? '' },
    { field: 'amountTaken',     header: 'AmountTaken', width: 115, type: 'number',
      text: r => String(r.amountTaken), value: r => r.amountTaken },
    { field: 'amountRemain',    header: 'AmountLeft',  width: 110, type: 'number',
      text: r => String(r.amountRemain), value: r => r.amountRemain },
    /* "Consumed" is ConsumedBy (the legacy header is just clipped), NOT the
       row's ConsumedLT -- the model's ColumnInfo is
       `Id="ConsumedBy" LookupProperty="DisplayName"`. TakenBy beside it is a
       DIFFERENT person, so both columns are real. */
    { field: 'consumedByName',  header: 'Consumed',    width: 150,
      text: r => r.consumedByName ?? '', render: r => r.consumedByName ?? '' },
    { field: 'takenByName',     header: 'TakenBy',     width: 150,
      text: r => r.takenByName ?? '', render: r => r.takenByName ?? '' },
    { field: 'comment',         header: 'Comment',     width: 200,
      text: r => r.comment ?? '', render: r => r.comment ?? '' },
    { field: 'lastUpdatedByName', header: 'LastUpdatedBy', width: 150,
      text: r => r.lastUpdatedByName ?? '', render: r => r.lastUpdatedByName ?? '' },
    { field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: 150,
      text: r => fmtLocalShort(r.lastUpdatedDate) ?? '',
      render: r => fmtLocalShort(r.lastUpdatedDate) ?? '' },
  ];

  /** The popup's VRCUploads tab -- the certificate's own PDFs, read-only here
   *  (the parent's Upload tab owns New / Delete / Download). */
  const vrcUploadCols: AppColumn<VendorCoaAttachment>[] = [
    { field: 'fileName',       header: 'File',        width: 360,
      text: r => r.fileName ?? '', render: r => r.fileName ?? '' },
    { field: 'sizeBytes',      header: 'Size',        width: 110, type: 'number',
      text: r => String(r.sizeBytes ?? 0), value: r => r.sizeBytes ?? 0 },
  ];

  /** The LT tab's single column (legacy shows only LT#). */
  const ltCols: AppColumn<{ oid: string; lt: string | null }>[] = [
    { field: 'lt', header: 'LT#', width: 200, text: r => r.lt ?? '', render: r => r.lt ?? '' },
  ];

  /** Loaded By / Loaded Date -- the detail GET carries them; the list row no
   *  longer does (those columns were removed from the grid). */
  const [loadedBy, setLoadedBy] = useState('');
  const [loadedDate, setLoadedDate] = useState<string | null>(null);

  /** The ONE detail action bar (standing rule 12 / §18.6), rendered IDENTICALLY
   *  at the top and the bottom in the canonical order: the form-specific action
   *  (Import Certificates) · Refresh · the Save set. Legacy splits Refresh /
   *  Import Certificates onto their own row above a right-aligned Save set; the
   *  AI standard is a single bar, so the two rows are merged here. Shared
   *  `sd-actionbar` / `sd-abtn` classes, so the bar is pixel-identical to every
   *  other AI detail form (standing rule 9). The hidden file input that Import
   *  Certificates drives is rendered ONCE in the detail body — several <label>s
   *  may share one htmlFor, so both copies open the same picker. */
  const detailActions = (bottom = false) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {perm.write && <label className="sd-abtn" htmlFor="vrc-import"
             style={{ cursor: importing ? 'wait' : 'pointer' }}
             title="Attach each PDF to every certificate whose Destination is that file name">
        <i className="ti ti-cloud-upload" /> {importing ? 'Importing…' : 'Import Certificates'}
      </label>}
      <button className="sd-abtn" disabled={loading}
              onClick={() => { if (dialog?.id) void loadChildren(dialog.id); void load(); }}>
        <i className="ti ti-refresh" /> Refresh
      </button>
      {perm.write && <button className="sd-abtn primary" onClick={() => void handleSave(false)}>
        <i className="ti ti-device-floppy" /> Save
      </button>}
      {perm.write && <button className="sd-abtn" onClick={() => void handleSave(true)}>
        <i className="ti ti-device-floppy" /> Save and Close
      </button>}
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={() => setDialog(null)}>
        <i className="ti ti-x" /> Cancel
      </button>
    </div>
  );

  // Option lists
  const [vendors, setVendors] = useState<IdNameOption[]>([]);
  const [items, setItems] = useState<IdNameOption[]>([]);
  const [employees, setEmployees] = useState<IdNameOption[]>([]);


  /** Attach a certificate PDF to a record. Legacy does this from the DetailView
   *  (ShowNew_Execute -> FileUploadCollectionVRC), never from the list, and the
   *  ListView itself is AllowNew="False" -- the row is made by the distribution
   *  flow. */
  async function uploadCoa(id: string, fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setImporting(true);
    try {
      await api.post(`/api/v1/vendor-coas/${id}/attachments`, fd);
      notify.success('1 PDF uploaded successfully.');
      await loadChildren(id);
      await load();
    } catch {
      notify.error('Upload failed.');
    } finally { setImporting(false); }
  }

  /* importCertificates() removed from this page: legacy's ImportCertificates
     action targets VendorReagentCertificate_DETAILView, not the list, and the
     list bar in GoLive does not carry it. POST /api/v1/vendor-coas/
     import-certificates is still there for when the detail view gains it. */


  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      // take: the shared ICM ceiling, like every sibling list. Without it the server default of
      // 50 applied and the grid reported "50 items" over a table holding 1,184 --
      // the page silently showed the first 50 and paginated inside them.
      const res = await api.get('/api/v1/vendor-coas', {
        params: {
          take: ICM_LIST_TAKE,
          history: history || undefined,
          // Same rule as Item Consumption: the picker is in History only, so the
          // window is sent there only. It was filtering the MAIN list too --
          // 51 certificates of 1,193, behind a control the user cannot see.
          ...(history ? { months: monthsOf(dateFilter) } : {}),
        },
      });
      const data = (res.data as { items?: VendorCoaRow[] });
      setRows(data.items ?? []);
    } catch {
      setError('Failed to load vendor COAs.');
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateFilter, history]);

  const loadOptions = useCallback(async () => {
    try {
      const [vRes, iRes, eRes] = await Promise.all([
        api.get('/api/v1/vendor-coas/vendor-options'),
        api.get('/api/v1/requisitions/items'),
        api.get('/api/v1/distributions/employee-options'),
      ]);
      const toArr = (d: unknown): unknown[] => Array.isArray(d) ? d : ((d as { data?: unknown[] })?.data ?? []);
      const mapVendors = toArr(vRes.data) as Array<{ oid?: string; vendorName?: string }>;
      setVendors(mapVendors.map(v => ({ id: v.oid ?? '', name: v.vendorName ?? '' })));

      const mapItems = toArr(iRes.data) as Array<{ oid?: string; itemName?: string }>;
      setItems(mapItems.map(i => ({ id: i.oid ?? '', name: i.itemName ?? '' })));

      const mapEmps = toArr(eRes.data) as Array<{ oid?: string; displayName?: string; firstName?: string; lastName?: string }>;
      setEmployees(mapEmps.map(e => ({
        id: e.oid ?? '',
        name: e.displayName ?? [e.firstName, e.lastName].filter(Boolean).join(' ') ?? '',
      })));
    } catch {
      notify.error('Failed to load lookup options.');
    }
  }, [notify]);

  useEffect(() => { void load(); void loadOptions(); }, [load, loadOptions]);

  /** The top-bar title follows the view, as G5's does (ShowViewModeVRC_Execute appends
   *  " History" to the caption). Uses the shared `ai:topbar-title` override AppShell
   *  listens for. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Vendors COA History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.vendorLt ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.rqid ?? '').toLowerCase().includes(t) ||
      (r.requestorName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /** Prev / next record -- the green arrows GoLive puts at the detail's top
   *  right (standing rule 12). They walk the rows the list currently holds. */
  const detailIdx = dialog?.id ? filtered.findIndex(r => r.oid === dialog.id) : -1;
  const gotoRecord = async (delta: number) => {
    if (detailIdx < 0) return;
    const next = filtered[detailIdx + delta];
    if (!next) return;
    setSelected(new Set([next.oid]));
    await openEditById(next.oid);
  };


  const cols: AppColumn<VendorCoaRow>[] = [
    /* LIVE GOLIVE GRID, in its order: VendorLT . Item . Vendor . Catalog# .
       Department . RQ# . PO# . RC# . Requestor.
       The app-level BTLIMS.Web\Model.xafml marks LoadedBy ("UploadedBy"),
       LoadedDate, Item.Specification ("Description") and Division with
       Index="-1", i.e. HIDDEN -- only BTLIMS.Module.Web\Model.DesignedDiffs.xafml
       lists them, and the app model overrides the module diff. They were added
       from the wrong file and are gone again; the running grid is authoritative
       (owner report 2026-09-28). Department, which IS on the live grid, was the
       one genuinely missing column. */
    { field: 'vendorLt',      header: 'VendorLT',   width: 140, text: r => r.vendorLt ?? '—', render: r => r.vendorLt ?? '—' },
    { field: 'itemName',      header: 'Item',       width: 220, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'vendorName',    header: 'Vendor',     width: 180, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'catelog',       header: 'Catalog#',   width: 140, text: r => r.catelog ?? '—', render: r => r.catelog ?? '—' },
    { field: 'department',    header: 'Department', width: 140, text: r => r.department ?? '—', render: r => r.department ?? '—' },
    { field: 'rqid',          header: 'RQ#',        width: 120, text: r => r.rqid ?? '—', render: r => r.rqid ?? '—' },
    { field: 'poidText',      header: 'PO#',        width: 120, text: r => r.poidText ?? '—', render: r => r.poidText ?? '—' },
    { field: 'receiveId',     header: 'RC#',        width: 120, text: r => r.receiveId ?? '—', render: r => r.receiveId ?? '—' },
    { field: 'requestorName', header: 'Requestor',  width: 160, text: r => r.requestorName ?? '—', render: r => r.requestorName ?? '—' },
  ];

  /* openNew() and deleteSelected() are gone: legacy's ListView is
     AllowNew="False" AllowDelete="False", so this form never creates or deletes
     a certificate record -- the distribution flow does. Its "New" is the PDF
     upload above. The Save/Delete API endpoints were removed with them. */

  async function openEdit() {
    if (selected.size !== 1) { notify.warning('Select one row to edit.'); return; }
    await openEditById(String(Array.from(selected)[0]!));
  }

  async function openEditById(id: string) {
    try {
      const res = await api.get(`/api/v1/vendor-coas/${id}`);
      const d = (res.data as { data?: VendorCoaDetail })?.data ?? res.data as VendorCoaDetail;
      setForm({
        vendorLt: d.vendorLt ?? '',
        vendorId: d.vendorId ?? '',
        itemId: d.itemId ?? '',
        catelog: d.catelog ?? '',
        requestorId: d.requestorId ?? '',
        department: d.department ?? '',
        rqid: d.rqid ?? '',
        poId: d.poId ?? '',
        receiveId: d.receiveId ?? '',
        comments: d.comments ?? '',
      });
      setLoadedBy(d.loadedByName ?? '');
      setLoadedDate(d.loadedDate ?? null);
      setDialog({ mode: 'edit', id });
      setDetailTab('upload');
      await loadChildren(id);
    } catch {
      notify.error('Failed to load record for editing.');
    }
  }

  /** The two tabs legacy draws under the fields: Upload (the certificate PDFs)
   *  and LT (the lots this certificate covers). */
  const loadChildren = useCallback(async (id: string) => {
    try {
      const [a, l] = await Promise.all([
        api.get(`/api/v1/vendor-coas/${id}/attachments`),
        api.get(`/api/v1/vendor-coas/${id}/linked-distributions`),
      ]);
      setAttachments((a.data ?? []) as VendorCoaAttachment[]);
      setLts((l.data ?? []) as { oid: string; lt: string | null }[]);
    } catch {
      setAttachments([]); setLts([]);
    }
  }, []);

  /** Opens the read-only lot popup behind an LT# row.
   *
   *  Legacy activates the row through XAF's stock ProcessCurrentObject, which
   *  opens the Distribution detail captioned "Fractional Consumption" as a
   *  modal. AI reserves a single click for selection, so this is on DOUBLE
   *  click -- which the shared grid also maps to Enter, keeping it reachable
   *  from the keyboard (standing rule 14). Same pattern as the Ordering Items
   *  "Item Requisition" popup.
   *
   *  It reuses the EXISTING fractional-consumption endpoint rather than adding
   *  one: `GET /api/v1/fractional-consumptions/{id}` already returns exactly
   *  this pair -- the Distribution header and its ItemDepletion rows -- because
   *  the Fractional Consumption screen reads the same lot. */
  const openLtPopup = useCallback(async (row: { oid: string; lt: string | null }) => {
    setLtDetail(null);
    setLtDepletions([]);
    setLtVendorLt('');
    setLtOid(row.oid);
    setLtPopupTab('consumption');
    setLtPopupOpen(true);
    try {
      const res = await api.get(`/api/v1/fractional-consumptions/${row.oid}`);
      const d = res.data as { distribution?: LtDetail; depletions?: DepletionRow[] };
      setLtDetail(d.distribution ?? null);
      setLtDepletions(d.depletions ?? []);
      setLtVendorLt(d.distribution?.vendorLt ?? '');
    } catch {
      setLtPopupOpen(false);
      notify.error('Failed to open that LT.');
    }
  }, [notify]);

  /** Opens the Vendor reference popup (legacy: the Vendor hyperlink). */
  const openVendorRef = useCallback(async (vendorId: string) => {
    setVendorRef(null);
    setRefPopup('vendor');
    try {
      const res = await api.get(`/api/v1/fractional-consumptions/vendor-ref/${vendorId}`);
      setVendorRef(res.data as VendorRef);
    } catch {
      setRefPopup(null);
      notify.error('Failed to open that vendor.');
    }
  }, [notify]);

  /** Opens the Storage reference popup (legacy: the Storage hyperlink). */
  const openStorageRef = useCallback(async (storageId: string) => {
    setStorageRef(null);
    setRefPopup('storage');
    try {
      const res = await api.get(`/api/v1/fractional-consumptions/storage-ref/${storageId}`);
      setStorageRef(res.data as StorageRef);
    } catch {
      setRefPopup(null);
      notify.error('Failed to open that storage location.');
    }
  }, [notify]);

  /** OK. Legacy's dialog runs with the default SaveOnAccept = true, so OK
   *  commits the Vendor Lot edit and Cancel (the ✕ / Esc / backdrop) discards
   *  it. The server no-ops -- and writes no audit -- when nothing changed, so
   *  an OK on an untouched popup costs one idempotent call and changes
   *  nothing. */
  async function acceptLtPopup() {
    const dirty = ltOid && ltDetail && (ltVendorLt !== (ltDetail.vendorLt ?? ''));
    if (!dirty) { setLtPopupOpen(false); return; }
    setLtSaving(true);
    try {
      await api.put(`/api/v1/fractional-consumptions/${ltOid}/vendor-lt`, { vendorLt: ltVendorLt });
      notify.success('Saved successfully.');
      setLtPopupOpen(false);
      if (dialog?.id) await loadChildren(dialog.id);
    } catch (err) {
      notify.error(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Save failed.');
    } finally { setLtSaving(false); }
  }

  /** Downloads one certificate PDF.
   *
   *  MUST go through the axios client, not an <a href>. The JWT rides an
   *  Authorization HEADER, not a cookie, so a plain link (or a new-tab
   *  navigation) sends no credentials and the endpoint answers 401 — verified
   *  here, and the same trap is already recorded for LT# COA Tracking's COA
   *  button. The toolbar "Download" on this tab was an <a href> too and had the
   *  same defect; both now come through here. Saving the blob reuses the shared
   *  utils/exportData downloadBlob so no page re-implements it. */
  async function downloadAttachment(a: VendorCoaAttachment) {
    if (!a.fileDataOid) return;
    try {
      const res = await api.get<Blob>(`/api/v1/vendor-coas/file/${a.fileDataOid}/download`,
                                      { responseType: 'blob' });
      downloadBlob(res.data, a.fileName ?? 'certificate.pdf');
    } catch { notify.error('Failed to download the certificate.'); }
  }

  /** Deletes EVERY ticked attachment, not just a lone one. The toolbar button
   *  used to key off a `uploadSel.size === 1` value, so ticking two rows made it
   *  null and greyed Delete out -- the grid let you multi-select and then
   *  refused to act on it (owner report 2026-09-29). The grid is `selectable`
   *  with a select-all header box, so the set is the unit of work here. */
  async function removeAttachments(uploadOids: string[]) {
    if (!dialog?.id || uploadOids.length === 0) return;
    const many = uploadOids.length > 1;
    if (!(await confirmDelete({
      title: many ? `Delete ${uploadOids.length} attachments?` : 'Delete attachment?',
      message: many
        ? `Remove these ${uploadOids.length} certificates from the record?`
        : 'Remove this certificate from the record?',
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    try {
      // Sequential, not Promise.all: each DELETE retires the shared upload row
      // only when nothing else points at it, and that check must not race
      // itself when several rows go at once.
      for (const oid of uploadOids) {
        await api.delete(`/api/v1/vendor-coas/${dialog.id}/attachments/${oid}`);
      }
      notify.success('Deleted successfully.');
      setUploadSel(new Set());
      await loadChildren(dialog.id);
      await load();
    } catch { notify.error('Delete failed.'); }
  }

  /** Legacy's "Import Certificates", which targets THIS detail view: each PDF is
   *  attached to every certificate whose Destination equals its file name. */
  async function importCertificates(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const fd = new FormData();
    Array.from(fileList).forEach(f => fd.append('files', f));
    setImporting(true);
    try {
      const res = await api.post('/api/v1/vendor-coas/import-certificates', fd);
      const { attached = 0, unmatched = [] } = (res.data ?? {}) as { attached?: number; unmatched?: string[] };
      notify.success(`${attached} PDFs uploaded successfully.`);
      if (unmatched.length > 0) notify.warning(`No certificate matched: ${unmatched.join(', ')}`);
      if (dialog?.id) await loadChildren(dialog.id);
      await load();
    } catch { notify.error('Import failed.'); }
    finally { setImporting(false); }
  }


  async function handleSave(close: boolean) {
    if (!dialog) return;
    const payload = {
      ...form,
      vendorId: form.vendorId || null,
      itemId: form.itemId || null,
      requestorId: form.requestorId || null,
      poId: form.poId || null,
    };
    await saveFlow({
      formName: 'Vendors COA',
      auditAction: dialog.mode === 'new' ? 'create' : 'edit',
      save: async () => {
        if (dialog.mode === 'new') {
          await api.post('/api/v1/vendor-coas', payload);
        } else {
          await api.put(`/api/v1/vendor-coas/${dialog.id}`, payload);
        }
      },
      onSaved: async () => {
        if (close) setDialog(null);
        await load();
      },
    });
  }

  function setF<K extends keyof CoaForm>(k: K, v: CoaForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  return (
    <div className="bl-page">
      {/* LIST ONLY. Items shows no page-level header on its detail -- the panel's
          own sd-head breadcrumb carries Home > group > form > record, so
          rendering this block as well put a SECOND breadcrumb (and the load
          chip) above the panel and pushed the whole detail ~68px down. */}
      {!dialog && (
      <div className="bl-top">
        <div className="dp-page-hdr">
          {/* In HISTORY the trail gains a "History" crumb and "Vendors COA"
              becomes the way back to All Records — the toolbar no longer offers
              it. GoLive leaves its History view the same way, through the
              breadcrumb rather than a toggle button. */}
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {history ? (
              <>
                <a onClick={() => setHistoryMode(false)}
                   style={{ cursor: 'pointer' }}
                   title="Back to all records">Vendors COA</a>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">History</span>
              </>
            ) : (
              <span className="dp-bc-active">Vendors COA</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-vendor-coas" auditFormName="Vendors COA" title={history ? 'Vendors COA History' : 'Vendors COA'} loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>
      )}

      {/* THE LIST IS REPLACED BY THE DETAIL, not pushed down by it (standing
          rule 12, and what GoLive does -- clicking Edit there leaves only the
          record on screen). The detail block was simply appended after this one,
          so the grid, its toolbar and its pager stayed above it. */}
      {!dialog && (
        <>
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* GoLive's bar is exactly: Edit . History . Refresh . Export to .
                Search. No New and no Delete -- the ListView is AllowNew="False"
                AllowDelete="False"; a certificate row is created by the
                DISTRIBUTION flow when a VendorLT is entered. No Date Filter, and no
                Import Certificates: legacy's ImportCertificates action targets
                VendorReagentCertificate_DETAILView, not this list (owner report
                2026-09-28). Both were on the list bar and are gone.
                The certificate PDF is attached from the Edit dialog, which is where
                legacy's Edit takes you as well. */}
            <AppToolbar
              left={
                history
                  /* HISTORY view: GoLive's bar here is Edit · Date Filter ·
                     Refresh · Export to — a Date Filter where the main list has
                     the History button, and NO "All Records" button (owner
                     request 2026-09-29). Leaving History is the breadcrumb's
                     "Vendors COA" crumb, mirroring GoLive, which also navigates
                     out of its History view rather than toggling on the bar. */
                  ? <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
                  : <button className="ca-btn"
                            onClick={() => setHistoryMode(true)}
                            title="Only certificates that have been uploaded">
                      <i className="ti ti-history" /> History
                    </button>
              }
              /* GoLive draws this bar Edit · History · Refresh · Export to --
                 Edit AHEAD of the form-specific History (verified side by side
                 against the live VendorReagentCertificate_ListView, owner report
                 2026-09-29). `editFirst` is the shared opt-in for exactly that,
                 mirroring the `newFirst` Item Requisition uses; the button, its
                 glyph and its Alt+E / F2 binding are unchanged. */
              editFirst
              onEdit={perm.write ? openEdit : undefined}
              editDisabled={selected.size !== 1}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'vendor-coas', ...gridExportMatrix(filtered, cols) }}
            />
          </div>
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor LT, item, RQID…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
            ) : (
              <AppDataGrid<VendorCoaRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                /* G5's row pencil (bug report 2026-11-02): opens the same detail as
                   the toolbar Edit, under the same Write right. */
                onRowOpen={perm.write ? r => { setSelected(new Set([r.oid])); void openEditById(r.oid); } : undefined}
                emptyText="No vendor COAs found."
              />
            )}
          </div>
        </div>
        </>
      )}

      {/* ── DETAIL VIEW (legacy VendorReagentCertificate_DetailView) ─────────
          A FULL-PAGE detail, not a popup (standing rule 12), following the AI
          detail standard rather than GoLive's chrome (owner decision
          2026-09-29): sd-head title + breadcrumb + prev/next arrows, then ONE
          action bar mirrored top and bottom. The FIELDS keep GoLive's shape --
          two columns, everything read-only except Comments, since these rows are
          created by the distribution flow and the only things the operator owns
          here are the comment and the certificate itself. Below them, legacy's
          two tabs: Upload (the PDFs, New/Delete/Download) and LT (the lots this
          certificate covers). */}
      {dialog && (
        /* The SHELL <SettingsDetailPage> builds for Items, reproduced exactly:
           .bl-grid-area > .sd-wrap (the white paneL: border, radius, shadow,
           flex column) > .sd-head, the TOP action bar, a scrolling .sd-body
           (padding 18px 22px) and the BOTTOM action bar. Both bars sit OUTSIDE
           .sd-body, so they stay put while the body scrolls -- the Items
           behaviour. This replaced a `bl-card` with an inline padding:20, which
           put the fields in a plainer box with no scroll region and no section
           header. */
        <>
        {/* The List View | Detail View switch Items shows ABOVE the panel. It is
            the shared <ViewToggle>, placed and padded exactly as ModuleListView
            places it on a detail (`padding: 10px 16px 0`, flex 0 0 auto), with
            "List View" returning to the grid -- the same thing Cancel does.
            This was the LAST measurable difference from Items: with the panel,
            body, section header and field grid all matching byte for byte, the
            switcher was the only property still reading false here and true
            there. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={v => { if (v === 'list') setDialog(null); }} />
        </div>
        <div className="bl-grid-area">
        <div className="sd-wrap">
          {/* Standard AI detail header (rule 12): "{BO} – {record}" + breadcrumb
              + prev/next record arrows, using the same sd-head markup as
              <SettingsDetailPage> so it matches every other detail form. */}
          <div className="sd-head">
            <div>
              <h2>Vendor Reagent Certificate – {form.vendorLt || '(new)'}</h2>
              {/* Home > group > form > record, the four levels Items' sd-bc
                  shows (Home > Settings > Items > Item_00002). Only the form
                  level is a link, back to this list -- the same one Items makes
                  clickable. */}
              <div className="sd-bc">
                <span>Home</span>
                <i className="ti ti-chevron-right" />
                <span>ICM Operations</span>
                <i className="ti ti-chevron-right" />
                <a onClick={() => setDialog(null)}>Vendors COA</a>
                <i className="ti ti-chevron-right" />
                <span>{form.vendorLt || 'Vendor Reagent Certificate'}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button className="sd-na" title="Previous record"
                      disabled={detailIdx <= 0}
                      onClick={() => void gotoRecord(-1)}>‹</button>
              <button className="sd-na next" title="Next record"
                      disabled={detailIdx < 0 || detailIdx >= filtered.length - 1}
                      onClick={() => void gotoRecord(1)}>›</button>
            </div>
          </div>

          {/* The one hidden picker BOTH copies of "Import Certificates" drive.
              Legacy declares that action with
              TargetViewId="VendorReagentCertificate_DetailView", which is why it
              lives on this detail and not on the list. */}
          <input id="vrc-import" type="file" accept="application/pdf,.pdf" multiple hidden
                 disabled={importing}
                 onChange={e => { void importCertificates(e.target.files); e.target.value = ''; }} />

          {detailActions()}

          <div className="sd-body">
          {/* NO section header (owner request 2026-09-29: "remove the GENERAL
              INFORMATION text"). This is a supported opt-out, not a deviation —
              <SettingsDetailPage> omits the same <h3 className="sd-section-h">
              for any form whose moduleCrud sets `hideSectionHeader`; Items does
              not set it, which is why the header was added here for parity, and
              this form now opts out the way that flag does. GoLive draws no such
              caption on this detail either. */}

          {/* FIELD LAYOUT — the Settings → Inventory Management → Items standard
              (standing rule 12, owner request 2026-09-29 "refer the form layout
              standard in Items"). Items is `set-inventory-items`, whose moduleCrud
              sets columnMajor + sideLabels, and <SettingsDetailPage> renders that
              as ONE grid carrying `sd-grid sd-grid--colmajor sd-grid--side` with
              `--sd-rows` = ceil(fieldCount / 2). Reproduced here EXACTLY rather
              than approximated: this used to be a bespoke `po-panels` wrapper (a
              PurchaseOrderDetailPage class, 40px column gap) holding TWO
              `sd-grid--single` grids, which only looked like the standard — the
              real one is a single column-major grid at the shared 28px gap, so
              the field columns line up with every other AI detail form.
              9 fields → --sd-rows 5, which fills the LEFT column with
              Vendor LT · Item · Vendor · Catalog · Requestor and the RIGHT with
              Department · Loaded By · Loaded Date · Comments — the same split as
              before, now produced by the standard instead of by hand.
              `sd-grid--side` supplies the rest of the standard for free: a fixed
              150px LEFT-aligned label column with its ":" appended by CSS, a 6px
              row-gap, and every control a uniform 26px — including the textarea,
              which is why it no longer carries rows={3}. */}
          <div className="sd-grid sd-grid--colmajor sd-grid--side"
               style={{ '--sd-rows': '5' } as CSSProperties}>
            <label className="sd-row"><span className="sd-label">Vendor LT</span>
              <input className="sd-input readonly" readOnly value={form.vendorLt} /></label>
            <label className="sd-row"><span className="sd-label">Item</span>
              <input className="sd-input readonly" readOnly
                     value={items.find(i => i.id === form.itemId)?.name ?? ''} /></label>
            <label className="sd-row"><span className="sd-label">Vendor</span>
              <input className="sd-input readonly" readOnly
                     value={vendors.find(v => v.id === form.vendorId)?.name ?? ''} /></label>
            <label className="sd-row"><span className="sd-label">Catalog</span>
              <input className="sd-input readonly" readOnly value={form.catelog} /></label>
            <label className="sd-row"><span className="sd-label">Requestor</span>
              <input className="sd-input readonly" readOnly
                     value={employees.find(e => e.id === form.requestorId)?.name ?? ''} /></label>

            <label className="sd-row"><span className="sd-label">Department</span>
              <input className="sd-input readonly" readOnly value={form.department} /></label>
            <label className="sd-row"><span className="sd-label">Loaded By</span>
              <input className="sd-input readonly" readOnly value={loadedBy} /></label>
            <label className="sd-row"><span className="sd-label">Loaded Date</span>
              <input className="sd-input readonly" readOnly value={fmtLocalDate(loadedDate) ?? ''} /></label>
            {/* The ONE editable field, as in legacy. */}
            <label className="sd-row"><span className="sd-label">Comments</span>
              <textarea className="sd-input" readOnly={!perm.write} value={form.comments}
                        onChange={e => setF('comments', e.target.value)} /></label>
          </div>

          {/* ── Upload / LT tabs ───────────────────────────────────────────── */}
          {/* REAL TABS, not a pair of buttons (owner request 2026-09-29 with a
              GoLive screenshot: Upload / LT are a tab strip attached to the top
              of the panel there, and these rendered as two detached .ca-btn
              pills above a separate card). Uses the SAME shared markup and CSS
              <SettingsDetailPage> gives a child grid — `sd-assoc-tabbar` +
              `sd-assoc-tab`/`.active` over a `sd-childtabpanel` — which is what
              draws a tab joined to its panel (the panel sets `border-top: none`
              because "the tab strip draws that edge", and the active tab is
              white with an orange top rule). That is the same component Items
              renders its "Sub Item" tab with, so this reads identically. */}
          <div className="sd-assoc-tabbar" role="tablist" style={{ marginTop: 18 }}>
            {(['upload', 'lt'] as const).map(t => (
              <button key={t}
                      type="button"
                      role="tab"
                      aria-selected={detailTab === t}
                      className={`sd-assoc-tab${detailTab === t ? ' active' : ''}`}
                      onClick={() => setDetailTab(t)}>
                {t === 'upload' ? 'Upload' : 'LT'}
              </button>
            ))}
          </div>

          <div className="sd-childtabpanel" role="tabpanel">
            {detailTab === 'upload' ? (
              <>
                <div className="sd-child-bar" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {perm.write && <label className="ca-btn" style={{ cursor: importing ? 'wait' : 'pointer' }}
                         title="Attach a certificate PDF">
                    <i className="ti ti-file-plus" /> New
                    <input type="file" accept="application/pdf,.pdf" hidden disabled={importing}
                           onChange={e => { void uploadCoa(dialog.id!, e.target.files); e.target.value = ''; }} />
                  </label>}
                  {perm.delete && <button className="ca-btn" disabled={uploadSel.size === 0}
                          onClick={() => void removeAttachments(Array.from(uploadSel).map(String))}>
                    <i className="ti ti-x" /> Delete
                  </button>}
                  <button className="ca-btn" disabled={!selectedUploadRow}
                          onClick={() => selectedUploadRow && void downloadAttachment(selectedUploadRow)}>
                    <i className="ti ti-download" /> Download
                  </button>
                </div>
                <AppDataGrid<VendorCoaAttachment>
                  rows={attachments}
                  /* GoLive puts a per-row download glyph in its own narrow column
                     and renders the file name as a LINK; AI showed the name as
                     plain text with an empty column beside it, so a row could
                     only be downloaded by ticking it and using the toolbar
                     (owner report 2026-09-29). Both now point at the same
                     endpoint the toolbar Download uses. A row whose blob is
                     missing (fileDataOid null) stays plain text rather than
                     offering a link that would 404. */
                  columns={[
                    { field: 'download', header: '', width: 40, align: 'center',
                      text: () => '',
                      render: r => r.fileDataOid ? (
                        <button type="button" className="vcoa-dl"
                                title={`Download ${r.fileName ?? 'this certificate'}`}
                                onClick={e => { e.stopPropagation(); void downloadAttachment(r); }}>
                          <i className="ti ti-download" />
                        </button>
                      ) : null },
                    { field: 'fileName', header: 'File', width: 420,
                      text: r => r.fileName ?? '',
                      render: r => r.fileDataOid ? (
                        <button type="button" className="vcoa-dl-name"
                                onClick={e => { e.stopPropagation(); void downloadAttachment(r); }}>
                          {r.fileName ?? ''}
                        </button>
                      ) : (r.fileName ?? '') },
                  ]}
                  getRowId={r => r.oid}
                  selectable
                  selectedIds={uploadSel}
                  onSelectionChange={setUploadSel}
                  emptyText="No data to display"
                />
              </>
            ) : (
              <>
                <div className="sd-child-bar">
                  <ExportMenu filename="vendor-coa-lts"
                              {...gridExportMatrix(lts, ltCols)} />
                </div>
                <AppDataGrid<{ oid: string; lt: string | null }>
                  rows={lts}
                  columns={ltCols}
                  getRowId={r => r.oid}
                  onRowDoubleClick={openLtPopup}
                  emptyText="No data to display"
                />
              </>
            )}
          </div>
          </div>{/* /sd-body */}

          {detailActions(true)}
        </div>
        </div>
        </>
      )}

      {/* ── LT row popup: legacy's "Fractional Consumption" modal ────────────
          Opened by activating an LT# row on the LT tab. READ-ONLY, like the
          Ordering Items "Item Requisition" popup: it is a viewer onto the lot,
          and the editable path for these depletions is the Fractional
          Consumption screen, which owns the buffered grid, the AmountTaken
          rules and the Save. OK simply closes.
          Field order is legacy's, top to bottom. */}
      <AppDialog
        open={ltPopupOpen}
        title="Fractional Consumption"
        icon="ti-flask"
        width={720}
        onClose={() => setLtPopupOpen(false)}
        footer={
          <>
            {perm.write && <button className="ca-btn" disabled={ltSaving} onClick={() => void acceptLtPopup()}>OK</button>}
            <button className="ca-btn ca-btn-ghost" disabled={ltSaving}
                    onClick={() => setLtPopupOpen(false)}>Cancel</button>
          </>
        }
      >
        {!ltDetail ? (
          <div className="bl-empty">Loading…</div>
        ) : (
          <>
            <div className="vcoa-lt-fields">
              <LtField label="LT"           value={ltDetail.lt} />
              <LtField label="Item"         value={ltDetail.itemName} />
              <LtField label="Specification" value={ltDetail.itemSpecification} />
              <LtField label="Size"         value={ltDetail.size} />
              <LtField label="Catalog#"     value={ltDetail.catalog} />
              <LtField label="Category"     value={ltDetail.category} />
              <LtField label="Stock Amount" value={ltDetail.itemAmount} />
              <div className="vcoa-lt-row">
                <span className="vcoa-lt-label">Is Deplete:</span>
                <input type="checkbox" checked={!!ltDetail.isDeplete} readOnly disabled />
              </div>
              {/* The ONE editable field -- see acceptLtPopup. */}
              <div className="vcoa-lt-row">
                <span className="vcoa-lt-label">Vendor Lot:</span>
                <input className="sd-input" type="text" style={{ maxWidth: 280 }} readOnly={!perm.write}
                       value={ltVendorLt} onChange={e => setLtVendorLt(e.target.value)} />
              </div>
              <LtField label="Expiry Date"  value={fmtLocalDate(ltDetail.expiryDate)} />
              {/* Vendor and Storage are LINKS, as legacy draws them: XAF renders
                  a read-only lookup editor as a hyperlink into the referenced
                  record, so each opens that record in a further popup. A row
                  with no id (or no value) stays plain text rather than offering
                  a link that would go nowhere. */}
              <LtRefField label="Vendor" value={ltDetail.vendorName}
                          onOpen={ltDetail.vendorId ? () => void openVendorRef(ltDetail.vendorId!) : undefined} />
              <LtRefField label="Storage" value={ltDetail.storageName}
                          onOpen={ltDetail.storageId ? () => void openStorageRef(ltDetail.storageId!) : undefined} />
            </div>

            <div className="sd-assoc-tabbar" role="tablist" style={{ marginTop: 16 }}>
              {(['consumption', 'uploads'] as const).map(t => (
                <button key={t} type="button" role="tab"
                        aria-selected={ltPopupTab === t}
                        className={`sd-assoc-tab${ltPopupTab === t ? ' active' : ''}`}
                        onClick={() => setLtPopupTab(t)}>
                  {t === 'consumption' ? 'Item Partial Consumption' : 'VRCUploads'}
                </button>
              ))}
            </div>
            <div className="sd-childtabpanel" role="tabpanel">
              {ltPopupTab === 'consumption' ? (
                <>
                  <div className="sd-child-bar">
                    <ExportMenu filename="lt-partial-consumption"
                                {...gridExportMatrix(ltDepletions, depPopupCols)} />
                  </div>
                  <AppDataGrid<DepletionRow>
                    rows={ltDepletions}
                    columns={depPopupCols}
                    getRowId={r => r.oid}
                    emptyText="No data to display"
                  />
                </>
              ) : (
                <>
                  <div className="sd-child-bar">
                    <ExportMenu filename="lt-vrc-uploads"
                                {...gridExportMatrix(attachments, vrcUploadCols)} />
                  </div>
                  {/* The uploads of the certificate this popup was opened from --
                      already loaded for the Upload tab, so no second request. */}
                  <AppDataGrid<VendorCoaAttachment>
                    rows={attachments}
                    columns={vrcUploadCols}
                    getRowId={r => r.oid}
                    emptyText="No data to display"
                  />
                </>
              )}
            </div>
          </>
        )}
      </AppDialog>

      {/* ── Vendor reference popup (the LT popup's Vendor link) ─────────────
          Read-only. Field order is Vendors_DetailView's two columns; its
          Evaluation / Upload tabs are not here -- this is a reference card
          reached from a lot, not the vendor's maintenance screen. */}
      <AppDialog
        open={refPopup === 'vendor'}
        title="Vendors"
        icon="ti-building-store"
        width={760}
        onClose={() => setRefPopup(null)}
        footer={<button className="ca-btn" onClick={() => setRefPopup(null)}>OK</button>}
      >
        {!vendorRef ? (
          <div className="bl-empty">Loading…</div>
        ) : (
          <div className="vcoa-ref-grid">
            <div>
              {/* NullText "Auto Generated" on the legacy editor. */}
              <LtField label="Vendor Code" value={vendorRef.vendorcode || 'Auto Generated'} />
              <LtField label="Vendor"      value={vendorRef.vendorName} />
              <LtField label="Account"     value={vendorRef.account} />
              <LtField label="Address 1"   value={vendorRef.address1} />
              <LtField label="Address 2"   value={vendorRef.address2} />
              <LtField label="City"        value={vendorRef.city} />
              <LtField label="State"       value={vendorRef.state} />
              <LtField label="State (CB)"  value={vendorRef.stateCbName} />
              <LtField label="Zip Code"    value={vendorRef.zipCode} />
              <LtField label="Country"     value={vendorRef.country} />
              <LtField label="Phone"       value={vendorRef.phone} />
              <LtField label="Fax"         value={vendorRef.fax} />
            </div>
            <div>
              <LtField label="Contact"       value={vendorRef.contact} />
              <LtField label="Email"         value={vendorRef.email} />
              <LtField label="Qualification" value={vendorRef.qualification} />
              <LtField label="Certificate"   value={vendorRef.certificate} />
              <LtField label="Cert Exp Date" value={fmtLocalDate(vendorRef.certExpDate)} />
              <LtField label="Approved Date" value={fmtLocalDate(vendorRef.approvedDate)} />
              <LtField label="Approved By"   value={vendorRef.approvedByName} />
              <LtField label="Retired Date"  value={fmtLocalDate(vendorRef.retiredDate)} />
              <LtField label="Retired By"    value={vendorRef.retiredByName} />
              <LtField label="Website"       value={vendorRef.website} />
              <LtField label="Comment"       value={vendorRef.comment} />
            </div>
          </div>
        )}
      </AppDialog>

      {/* ── Storage reference popup (the LT popup's Storage link) ──────────── */}
      <AppDialog
        open={refPopup === 'storage'}
        title="Storage"
        icon="ti-box"
        width={460}
        onClose={() => setRefPopup(null)}
        footer={<button className="ca-btn" onClick={() => setRefPopup(null)}>OK</button>}
      >
        {!storageRef ? (
          <div className="bl-empty">Loading…</div>
        ) : (
          <div className="vcoa-lt-fields">
            <LtField label="Storage"  value={storageRef.storageName} />
            <LtField label="Location" value={storageRef.location} />
            <div className="vcoa-lt-row">
              <span className="vcoa-lt-label">Active:</span>
              <input type="checkbox" checked={!!storageRef.active} readOnly disabled />
            </div>
            <LtField label="Comment"  value={storageRef.comment} />
          </div>
        )}
      </AppDialog>
    </div>
  );
}

/** A read-only pair whose value OPENS a referenced record when there is one to
 *  open -- legacy's hyperlink lookup. Without an id it degrades to plain text,
 *  so the popup never offers a link that goes nowhere. */
function LtRefField({ label, value, onOpen }:
  { label: string; value: string | null | undefined; onOpen?: () => void }) {
  return (
    <div className="vcoa-lt-row">
      <span className="vcoa-lt-label">{label}:</span>
      {onOpen && value ? (
        <button type="button" className="vcoa-lt-link" onClick={onOpen}>{value}</button>
      ) : (
        <span className="vcoa-lt-value">{value ?? ''}</span>
      )}
    </div>
  );
}

/** One read-only label/value pair in the LT popup. An empty value renders as a
 *  blank cell, as legacy does -- these are snapshot columns that are genuinely
 *  null on many lots, and "N/A" would read as data. */
function LtField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="vcoa-lt-row">
      <span className="vcoa-lt-label">{label}:</span>
      <span className="vcoa-lt-value">{value ?? ''}</span>
    </div>
  );
}
